import React, { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/captions.css';

export interface HelpImage {
  src: string;
  alt: string;
  caption?: string;
}

interface HelpScreenshotProps {
  images: HelpImage[];
  stepLabel?: string;
}

const HelpScreenshot: React.FC<HelpScreenshotProps> = ({ images, stepLabel }) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const mainImage = images[0];
  const hasMultiple = images.length > 1;

  const slides = images.map((img) => ({
    src: img.src,
    alt: img.alt,
    title: img.caption || img.alt,
  }));

  return (
    <>
      <div style={{ margin: '20px 0' }}>
        {/* Ana görsel */}
        <div
          style={{
            position: 'relative',
            cursor: 'pointer',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
          onClick={() => { setIndex(0); setOpen(true); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && (setIndex(0), setOpen(true))}
          aria-label={`View ${mainImage.alt} fullscreen`}
        >
          <img
            src={mainImage.src}
            alt={mainImage.alt}
            style={{ width: '100%', display: 'block', borderRadius: '12px' }}
          />
          {/* Zoom overlay */}
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'rgba(0,0,0,0.5)',
            borderRadius: '6px',
            padding: '4px 8px',
            color: '#fff',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            🔍 {hasMultiple ? `1 / ${images.length}` : 'Expand'}
          </div>
        </div>

        {/* Thumbnail grid (sadece çoklu görsellerde) */}
        {hasMultiple && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            gap: '8px',
            marginTop: '8px',
          }}>
            {images.slice(1).map((img, i) => (
              <div
                key={i}
                onClick={() => { setIndex(i + 1); setOpen(true); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && (setIndex(i + 1), setOpen(true))}
                aria-label={`View ${img.alt}`}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0',
                  aspectRatio: '16/9',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.03)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
                }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Alt etiket */}
        {stepLabel && (
          <p style={{
            fontSize: '12px',
            color: '#94a3b8',
            margin: '6px 0 0',
            textAlign: 'center' as const,
          }}>
            {stepLabel}{hasMultiple ? ` — ${images.length} screenshots` : ''}
          </p>
        )}
      </div>

      {/* Lightbox */}
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
        plugins={[Thumbnails, Captions, Zoom]}
      />
    </>
  );
};

export default HelpScreenshot;
